import { useState } from 'react';
import {
  INSTRUMENT_CLASSES,
  INSTRUMENT_CLASS_LABELS,
  INSTRUMENT_TIERS,
  INSTRUMENT_TIER_LABELS,
  isPriceable,
  requiredTermsFor,
  tierIsDowngrade,
  tierRequiresReviewer,
  type Entity,
  type Instrument,
  type InstrumentClass,
  type InstrumentClassification,
  type InstrumentTier,
} from '@ave/shared';
import { api } from '../lib/api';
import { TermField, termLabel } from './instrumentTerms';
import { Badge, Card, EmptyState, SectionHeading } from './ui';
import { Icon } from './icons';

const INPUT =
  'mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500';
const SELECT =
  'mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500';

const TIER_SHORT: Record<InstrumentTier, string> = {
  t1_quoted: 'Level 1',
  t2_observable: 'Level 2',
  t3_judgement: 'Level 3',
};

/**
 * Paperwork read into a proposal, and the two things a person can do with
 * one.
 *
 * The card exists because of a rule the API enforces and a screen has to
 * make visible: **a reading is never a record.** The classification decides
 * an IFRS 13 tier, and the tier decides whether the eventual price needs a
 * reviewer before anybody may use it — so a reading that created an
 * instrument by itself would be choosing how much scrutiny its own output
 * receives. What is on screen is therefore a proposal with a person's
 * decision still outstanding, and it is drawn to look like one.
 *
 * **The weak parts of the reading are the loudest part of the row.** The
 * confidence, the terms the paperwork did not settle, the inputs the
 * proposed model will need — those are the most useful thing the reading
 * produces, because they are what somebody has to go and find out. A
 * preparer shown only the class and the tier is being asked to accept a
 * judgement they cannot weigh.
 *
 * **The terms arrive filled in, and the tier does not arrive settled.**
 * That is the opposite of the held-allotment panel next door, where the
 * price the model read sits beside an empty field the preparer must type
 * into. The difference is what is being checked. There, one figure becomes
 * evidence directly and retyping it against the form *is* the check. Here
 * the terms are a structured transcription a preparer verifies by reading,
 * and the thing actually being accepted is a judgement about observability
 * — so the note is mandatory, and lowering the tier below what the reading
 * proposed is refused to anybody but a reviewer, because that is the edit
 * that takes a control away rather than adding one.
 */
export function InstrumentClassifications({
  engagementId,
  classifications,
  instruments,
  entities,
  canWrite,
  canReview,
  onChange,
}: {
  engagementId: string;
  classifications: InstrumentClassification[];
  /** What the confirmed readings became, so a resolved row can show both. */
  instruments: Instrument[];
  entities: Entity[];
  canWrite: boolean;
  canReview: boolean;
  onChange: () => void | Promise<void>;
}) {
  const open = classifications.filter((c) => c.status === 'proposed');
  const resolved = classifications.filter((c) => c.status !== 'proposed');
  const [showResolved, setShowResolved] = useState(false);

  return (
    <Card>
      <SectionHeading
        title="Paperwork awaiting a reading"
        subtitle="What the documents were read to say, before anybody accepted it"
        right={
          open.length > 0 ? (
            <Badge tone="amber">
              {open.length} proposed
            </Badge>
          ) : null
        }
      />

      {open.length === 0 ? (
        <EmptyState
          title="Nothing waiting"
          detail="Instrument paperwork read here is proposed, never recorded: the reading suggests the class, the fair value tier and the terms, and somebody confirms it against the document. Nothing reaches the library until they do."
        />
      ) : (
        <ul className="divide-y divide-slate-100">
          {open.map((classification) => (
            <li key={classification.id} data-item-id={classification.id} className="scroll-mt-24">
              <ProposalRow
                engagementId={engagementId}
                classification={classification}
                canWrite={canWrite}
                canReview={canReview}
                onChange={onChange}
              />
            </li>
          ))}
        </ul>
      )}

      {resolved.length > 0 ? (
        <div className="border-t border-slate-100 px-6 py-3">
          <button
            type="button"
            className="text-xs font-medium text-slate-500 hover:text-slate-700"
            onClick={() => setShowResolved((v) => !v)}
          >
            {showResolved ? 'Hide' : 'Show'} {resolved.length} resolved reading
            {resolved.length === 1 ? '' : 's'}
          </button>
          {showResolved ? (
            <ul className="mt-3 space-y-3">
              {resolved.map((c) => (
                <ResolvedRow
                  key={c.id}
                  classification={c}
                  became={instruments.find((i) => i.id === c.instrumentId) ?? null}
                />
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      {canWrite ? (
        <ReadPaperwork engagementId={engagementId} entities={entities} onSaved={onChange} />
      ) : null}
    </Card>
  );
}

/**
 * A reading somebody has answered.
 *
 * Both halves, where they differ. The reason for keeping a confirmed
 * proposal at all is that "the model read this as an employee option and we
 * recorded it as a lender warrant" is a fact about the file — and a row that
 * showed only what was proposed would make the reader go and find what it
 * became, while a row that showed only what was recorded would be the
 * library saying the same thing twice.
 */
function ResolvedRow({
  classification: c,
  became,
}: {
  classification: InstrumentClassification;
  became: Instrument | null;
}) {
  const amended =
    became !== null && (became.cls !== c.proposedClass || became.tier !== c.proposedTier);

  return (
    <li data-item-id={c.id} className="scroll-mt-24 text-sm">
      <div className="flex flex-wrap items-baseline gap-2">
        <Badge tone={c.status === 'confirmed' ? 'emerald' : 'slate'}>{c.status}</Badge>
        <span className="text-slate-700">
          {c.instrumentName} · read as {INSTRUMENT_CLASS_LABELS[c.proposedClass]},{' '}
          {TIER_SHORT[c.proposedTier]}
        </span>
        {amended ? (
          <span className="text-amber-700">
            · recorded as {INSTRUMENT_CLASS_LABELS[became!.cls]}, {TIER_SHORT[became!.tier]}
          </span>
        ) : null}
      </div>
      {/* A dismissed reading answers "why is that not in the library?",
          which is a question somebody asks years later. Its reason stays on
          screen rather than folding away with the row. */}
      {c.resolutionNote ? (
        <p className="mt-1 text-xs leading-relaxed text-slate-500">{c.resolutionNote}</p>
      ) : null}
    </li>
  );
}

type Mode = 'closed' | 'confirm' | 'dismiss';

function ProposalRow({
  engagementId,
  classification,
  canWrite,
  canReview,
  onChange,
}: {
  engagementId: string;
  classification: InstrumentClassification;
  canWrite: boolean;
  canReview: boolean;
  onChange: () => void | Promise<void>;
}) {
  const [mode, setMode] = useState<Mode>('closed');
  const c = classification;
  const confidence = c.extractionConfidence === null ? null : Number(c.extractionConfidence);

  return (
    <div className="px-6 py-4">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <span className="text-sm font-medium text-slate-800">{c.instrumentName}</span>
        <span className="text-xs text-slate-400">
          read from {c.paperworkChars.toLocaleString('en-GB')} characters of paperwork
        </span>
      </div>

      <div className="mt-2 flex flex-wrap gap-1.5">
        <Badge tone="slate">{INSTRUMENT_CLASS_LABELS[c.proposedClass]}</Badge>
        <Badge
          tone={tierRequiresReviewer(c.proposedTier) ? 'amber' : 'slate'}
          title={INSTRUMENT_TIER_LABELS[c.proposedTier]}
        >
          proposed {TIER_SHORT[c.proposedTier]}
        </Badge>
        {confidence !== null ? (
          <Badge
            tone={confidence >= 0.9 ? 'slate' : 'amber'}
            title="How completely and unambiguously the paperwork gave up its terms"
          >
            confidence {confidence.toFixed(2)}
          </Badge>
        ) : null}
        {/* Not decoration. It says the default model returned Level 3 and the
            question was then put to the top-tier one, which is a different
            answer from the same model saying Level 3 twice. */}
        {c.escalated ? (
          <Badge tone="slate" title="Read again on the escalation model because the first reading said Level 3">
            escalated
          </Badge>
        ) : null}
        {c.holder ? <Badge tone="slate">held by {c.holder}</Badge> : null}
      </div>

      <p className="mt-3 text-sm leading-relaxed text-slate-600">
        <span className="text-slate-500">Why that tier: </span>
        {c.tierRationale}
      </p>
      <p className="mt-1.5 text-sm leading-relaxed text-slate-600">
        <span className="text-slate-500">Model proposed: </span>
        {c.proposedModel}. {c.modelRationale}
      </p>

      {Object.keys(c.terms).length > 0 ? (
        <dl className="mt-3 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
          {Object.entries(c.terms).map(([key, value]) => (
            <div key={key} className="flex justify-between gap-3">
              <dt className="text-slate-500">{termLabel(key)}</dt>
              <dd className="tabular-nums text-slate-700">{String(value)}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {/* The point of the reading, not a caveat on it: these are the terms
          somebody now has to go and establish. */}
      {c.uncertain.length > 0 ? (
        <ul className="mt-3 space-y-1">
          {c.uncertain.map((u, i) => (
            <li key={i} className="flex gap-2 text-sm leading-relaxed text-slate-600">
              <Icon name="alert" className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
              <span>
                <span className="text-slate-800">{u.term}</span> — {u.reason}
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {c.observableInputsRequired.length > 0 ? (
        <p className="mt-3 text-sm leading-relaxed text-slate-500">
          The model will need {c.observableInputsRequired.join(', ')} — evidence chosen and
          justified when it is priced, not read off the paperwork.
        </p>
      ) : null}

      <p className="mt-3 text-xs text-slate-400">
        Read by {c.promptTemplate ?? 'an unrecorded template'}
        {c.modelId ? ` on ${c.modelId}` : ''} · paperwork sha256 {c.paperworkSha256.slice(0, 12)}…
      </p>

      {canWrite && mode === 'closed' ? (
        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" className="btn-primary" onClick={() => setMode('confirm')}>
            Check and record it
          </button>
          <button type="button" className="btn-secondary" onClick={() => setMode('dismiss')}>
            Dismiss
          </button>
        </div>
      ) : null}

      {mode === 'confirm' ? (
        <ConfirmForm
          engagementId={engagementId}
          classification={c}
          canReview={canReview}
          onClose={() => setMode('closed')}
          onSaved={async () => {
            setMode('closed');
            await onChange();
          }}
        />
      ) : null}

      {mode === 'dismiss' ? (
        <DismissForm
          engagementId={engagementId}
          classification={c}
          onClose={() => setMode('closed')}
          onSaved={async () => {
            setMode('closed');
            await onChange();
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Accepting the reading, with whatever the preparer changes about it.
 *
 * The tier field is the one that matters, so it is the one that argues back:
 * choosing a lower level than the reading proposed says plainly what that
 * removes, and is refused to anybody who is not a reviewer. Choosing a
 * higher one says nothing, because it costs only a reviewer's time.
 */
function ConfirmForm({
  engagementId,
  classification,
  canReview,
  onClose,
  onSaved,
}: {
  engagementId: string;
  classification: InstrumentClassification;
  canReview: boolean;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const c = classification;
  const [name, setName] = useState(c.instrumentName);
  const [holder, setHolder] = useState(c.holder ?? '');
  const [cls, setCls] = useState<InstrumentClass>(c.proposedClass);
  const [tier, setTier] = useState<InstrumentTier>(c.proposedTier);
  const [redeemable, setRedeemable] = useState(c.terms.redeemable !== false);
  const [terms, setTerms] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const [key, value] of Object.entries(c.terms)) {
      if (typeof value === 'string' || typeof value === 'number') initial[key] = String(value);
    }
    return initial;
  });
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The arrears terms appear only where the reading found an amount passed.
  // A reading that did not mention arrears is not evidence that there are
  // none, but it is not evidence that there are either, and asking a preparer
  // to rank arrears the document never mentioned invites an invented answer.
  const wanted = isPriceable(cls)
    ? requiredTermsFor(cls, {
        ...(cls === 'pref_share' ? { redeemable } : {}),
        ...(terms.arrears_amount ? { arrears_amount: terms.arrears_amount } : {}),
      })
    : [];
  const downgrading = tierIsDowngrade(c.proposedTier, tier);
  const blocked = downgrading && !canReview;
  const valid =
    name.trim() !== '' &&
    note.trim().length >= 10 &&
    !blocked &&
    wanted.every((key) => (terms[key] ?? '').trim() !== '');

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = {};
      for (const key of wanted) payload[key] = terms[key]!.trim();
      if (cls === 'pref_share') payload.redeemable = redeemable;

      await api.confirmClassification(engagementId, c.id, {
        note: note.trim(),
        name: name.trim(),
        holder: holder.trim() || undefined,
        cls,
        tier,
        // Only where the class has terms the engine will want. A class with
        // no model keeps what was read rather than losing it to an empty
        // form: the library records what exists, not only what is priced.
        ...(wanted.length > 0 ? { terms: payload } : {}),
      });
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not record the instrument');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50/60 p-4">
      <p className="text-sm font-medium text-slate-800">Check it against the document</p>
      <p className="mt-1 text-sm leading-relaxed text-slate-500">
        The fields below hold what the reading produced. Change whatever it got wrong — what you
        change is recorded against what it proposed.
      </p>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="text-sm text-slate-600">
          What it is called
          <input className={INPUT} value={name} onChange={(e) => setName(e.target.value)} />
        </label>

        <label className="text-sm text-slate-600">
          Held by
          <input
            className={INPUT}
            value={holder}
            placeholder="Optional"
            onChange={(e) => setHolder(e.target.value)}
          />
        </label>

        <label className="text-sm text-slate-600">
          Class
          <select
            className={SELECT}
            value={cls}
            onChange={(e) => setCls(e.target.value as InstrumentClass)}
          >
            {INSTRUMENT_CLASSES.map((option) => (
              <option key={option} value={option}>
                {INSTRUMENT_CLASS_LABELS[option]}
                {isPriceable(option) ? '' : ' — no model'}
              </option>
            ))}
          </select>
        </label>

        <label className="text-sm text-slate-600">
          Fair value hierarchy
          <select
            className={SELECT}
            value={tier}
            onChange={(e) => setTier(e.target.value as InstrumentTier)}
          >
            {INSTRUMENT_TIERS.map((option) => (
              <option key={option} value={option}>
                {INSTRUMENT_TIER_LABELS[option]}
              </option>
            ))}
          </select>
        </label>
      </div>

      {downgrading ? (
        <p className="mt-3 flex gap-2 text-sm leading-relaxed text-amber-700">
          <Icon name="alert" className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            The reading proposed {TIER_SHORT[c.proposedTier]} and this records{' '}
            {TIER_SHORT[tier]}, which takes away the scrutiny the higher level asks for.{' '}
            {canReview
              ? 'You are a reviewer, so it is yours to decide — say why below.'
              : 'A reviewer decides that, not a preparer.'}
          </span>
        </p>
      ) : null}

      {cls === 'pref_share' ? (
        <label className="mt-3 flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={redeemable}
            onChange={(e) => setRedeemable(e.target.checked)}
          />
          Redeemable — it has a redemption date. An irredeemable share is a perpetuity and needs
          none.
        </label>
      ) : null}

      {wanted.length > 0 ? (
        <>
          <p className="mt-4 text-sm font-medium text-slate-800">Terms, as read</p>
          <div className="mt-2 grid gap-3 sm:grid-cols-2">
            {wanted.map((key) => (
              <TermField
                key={key}
                termKey={key}
                value={terms[key] ?? ''}
                onChange={(value) => setTerms((t) => ({ ...t, [key]: value }))}
              />
            ))}
          </div>
        </>
      ) : null}

      <label className="mt-3 block text-sm text-slate-600">
        What you checked
        <textarea
          className={INPUT}
          rows={2}
          value={note}
          placeholder="Which clause you read, and what it said"
          onChange={(e) => setNote(e.target.value)}
        />
      </label>

      {error ? <p className="mt-3 text-sm text-rose-600">{error}</p> : null}

      <div className="mt-4 flex gap-2">
        <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button type="button" className="btn-primary" onClick={submit} disabled={!valid || busy}>
          Record this instrument
        </button>
      </div>
    </div>
  );
}

function DismissForm({
  engagementId,
  classification,
  onClose,
  onSaved,
}: {
  engagementId: string;
  classification: InstrumentClassification;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.dismissClassification(engagementId, classification.id, reason.trim());
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not dismiss the reading');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50/60 p-4">
      <label className="block text-sm text-slate-600">
        Why this is not an instrument to record
        <textarea
          className={INPUT}
          rows={2}
          value={reason}
          placeholder="Superseded, misread, already recorded, out of scope…"
          onChange={(e) => setReason(e.target.value)}
        />
      </label>
      <p className="mt-2 text-sm leading-relaxed text-slate-500">
        The reading is kept with your reason. "We read the articles and decided those growth shares
        were not a separate instrument" is a defensible position; silence is not.
      </p>

      {error ? <p className="mt-3 text-sm text-rose-600">{error}</p> : null}

      <div className="mt-4 flex gap-2">
        <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={submit}
          disabled={reason.trim().length < 10 || busy}
        >
          Dismiss it
        </button>
      </div>
    </div>
  );
}

/**
 * Handing the paperwork over.
 *
 * A textarea rather than a file picker, because there is no upload route and
 * nothing in the product yet reads a stored document. The copy says what
 * happens next in plain terms — a proposal, not a record — so that nobody
 * presses the button expecting an instrument to appear.
 */
function ReadPaperwork({
  engagementId,
  entities,
  onSaved,
}: {
  engagementId: string;
  entities: Entity[];
  onSaved: () => void | Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [entityId, setEntityId] = useState<string>(entities[0]?.id ?? '');
  const [name, setName] = useState('');
  const [holder, setHolder] = useState('');
  const [paperwork, setPaperwork] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The API's floor on pasted text. Named here so the button explains itself
  // before it is pressed rather than after: a sentence is not paperwork, and
  // a model handed one would classify it anyway.
  const MINIMUM = 200;
  const short = paperwork.trim().length < MINIMUM;
  const valid = entityId !== '' && name.trim() !== '' && (file !== null || !short);

  function reset() {
    setOpen(false);
    setName('');
    setHolder('');
    setPaperwork('');
    setFile(null);
    setStage(null);
  }

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      let sourceDocumentId: string | undefined;
      if (file !== null) {
        // Uploaded first, and separately. The document is worth keeping even
        // if the reading then fails — a valuation defends itself on the
        // paperwork, not on what a model made of it.
        setStage('Storing the document…');
        const buffer = await file.arrayBuffer();
        let binary = '';
        const view = new Uint8Array(buffer);
        for (let i = 0; i < view.length; i += 1) binary += String.fromCharCode(view[i]!);
        const uploaded = await api.uploadDocument(engagementId, {
          filename: file.name,
          mimeType: file.type || undefined,
          kind: 'instrument',
          contentBase64: btoa(binary),
        });
        sourceDocumentId = uploaded.document.id;
      }

      setStage('Reading it…');
      await api.classifyInstrument(engagementId, {
        entityId,
        instrumentName: name.trim(),
        holder: holder.trim() || undefined,
        ...(sourceDocumentId ? { sourceDocumentId } : { paperwork }),
      });
      reset();
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not read the paperwork');
    } finally {
      setBusy(false);
      setStage(null);
    }
  }

  if (!open) {
    return (
      <div className="border-t border-slate-100 px-6 py-4">
        <button type="button" className="btn-secondary" onClick={() => setOpen(true)}>
          Read some paperwork
        </button>
      </div>
    );
  }

  return (
    <div className="border-t border-slate-100 px-6 py-5">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-sm text-slate-600">
          Entity that issued it
          <select className={SELECT} value={entityId} onChange={(e) => setEntityId(e.target.value)}>
            {entities.map((entity) => (
              <option key={entity.id} value={entity.id}>
                {entity.name}
              </option>
            ))}
          </select>
        </label>

        <label className="text-sm text-slate-600">
          What to call it
          <input
            className={INPUT}
            value={name}
            placeholder="2024 unapproved option — A. Employee"
            onChange={(e) => setName(e.target.value)}
          />
        </label>

        <label className="text-sm text-slate-600 sm:col-span-2">
          Held by
          <input
            className={INPUT}
            value={holder}
            placeholder="Optional"
            onChange={(e) => setHolder(e.target.value)}
          />
        </label>
      </div>

      {/* The file is offered first because it is the better answer. An
          uploaded document is kept, and the proposal, the row and the stored
          object all name the same bytes; pasted text is a reading of
          something nobody has. */}
      <label className="mt-4 block text-sm text-slate-600">
        The document
        <input
          type="file"
          className="mt-1.5 block w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border file:border-slate-200 file:bg-white file:px-3 file:py-2 file:text-sm file:text-slate-700 hover:file:bg-slate-50"
          accept=".pdf,.txt,.md,.html,application/pdf,text/plain,text/markdown"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
      </label>
      {file !== null ? (
        <p className="mt-1.5 text-sm text-slate-500">
          {file.name} — {(file.size / 1024).toFixed(0)}KB. It is stored before it is read, so it
          stays on the file whatever the reading makes of it.
        </p>
      ) : null}

      <label className="mt-3 block text-sm text-slate-600">
        {file === null ? 'Or paste the paperwork' : 'Paperwork (not used — the document is)'}
        <textarea
          className={`${INPUT} font-mono text-xs`}
          rows={file === null ? 10 : 3}
          value={paperwork}
          disabled={file !== null}
          placeholder="Paste the option agreement, the loan note instrument, or the article that creates the class…"
          onChange={(e) => setPaperwork(e.target.value)}
        />
      </label>

      <p className="mt-2 text-sm leading-relaxed text-slate-500">
        This proposes a class, a fair value tier and the terms, and says why for each. It records
        nothing: the tier decides whether the price will need a reviewer, so somebody has to accept
        it against the document first. The reading is never asked for a volatility or a rate —
        those are not in the paperwork.
      </p>

      {file === null && short && paperwork.trim().length > 0 ? (
        <p className="mt-2 text-sm text-amber-700">
          {MINIMUM - paperwork.trim().length} more characters. A line or two is not paperwork, and a
          reading of it would be a guess with a confidence score on it.
        </p>
      ) : null}

      {error ? <p className="mt-3 text-sm text-rose-600">{error}</p> : null}

      <div className="mt-4 flex gap-2">
        <button type="button" className="btn-secondary" onClick={reset} disabled={busy}>
          Cancel
        </button>
        <button type="button" className="btn-primary" onClick={submit} disabled={!valid || busy}>
          {busy ? (stage ?? 'Reading…') : 'Read it'}
        </button>
      </div>
    </div>
  );
}
